import {
  countryName,
  DatePick,
  getPlan,
  type DayNote,
  getWork,
  NotFound,
  Popover,
  setWork,
  Suggest,
  toIso,
  type Plan,
  type WorkHours,
} from '@trippath/shared';
import { Fragment, memo, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import {
  clockOf,
  minutesOf,
  partOf,
  placesOf,
  verdictOf,
  wrap,
  type Part,
  type Verdict,
  PARTS,
  SHADES,
  type Place,
} from './clocks';
import { dayRange, offsetMinutes, plusDays, utcOffset } from '../../lib/countryFacts';
import { ModeIcon } from '../../components/icons/ModeIcon';
import { NOON, SUNRISE, SUNSET } from '../../components/icons/SunIcons';
import { useLive } from '../../app/live';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

/** Working hours a day, by half hours. */
const HALVES = Array.from({ length: 49 }, (_, at) => at * 30);

/** A clock the trip keeps in a country: its places on it. */
interface Clock {
  key: string;
  country: string;
  offset: number;
  zone: string;
  places: Place[];
}

/** A day's minutes, and the two days' hours. */
const DAY = 1440;
const HOURS = 48;

/**
 * The slider's track: each of the two days' hours as it is for all the clocks compared and home's
 * together — green while it is day everywhere, yellow while it is early or late somewhere, red
 * while it is night somewhere.
 */
function trackOf(shifts: number[]): string {
  const colour: Record<Verdict, string> = {
    day: 'var(--part-afternoon)',
    evening: 'var(--part-evening)',
    late: 'var(--part-late)',
    night: 'var(--part-night)',
  };
  const stops = Array.from({ length: HOURS }, (_, hour) => {
    const shades = shifts.map((shift) => SHADES[partOf(hour * 60 + shift)]);
    const worst: Verdict = shades.includes('night')
      ? 'night'
      : shades.includes('late')
        ? 'late'
        : shades.includes('evening')
          ? 'evening'
          : 'day';
    const from = (hour / HOURS) * 100;
    const to = ((hour + 1) / HOURS) * 100;
    return `${colour[worst]} ${from.toFixed(2)}% ${to.toFixed(2)}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/** How far a clock is from home's, never the same: `+5 h`, `−1:30 h`. */
function shiftSaid(minutes: number): string {
  const whole = Math.abs(minutes);
  const rest = whole % 60;
  return `${minutes > 0 ? '+' : '−'}${String(Math.floor(whole / 60))}${rest === 0 ? '' : `:${String(rest).padStart(2, '0')}`} h`;
}

/** Each part of the day drawn, after Lucide's (ISC licence). */
const DAY_PATHS: Record<Part, string> = {
  night: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z',
  dawn: SUNRISE,
  morning:
    'M10 2v2M14 2v2M6 2v2M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1',
  midday: NOON,
  lunch:
    'M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7',
  afternoon:
    'M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16M4 6h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
  evening: SUNSET,
  late: 'M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9',
};

/** A part of the day, drawn. */
function DayPartIcon({ part }: { part: Part }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={DAY_PATHS[part]} />
    </svg>
  );
}

const PART_PATHS: Record<Verdict, string> = {
  late: DAY_PATHS.late,
  day: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41',
  evening:
    'M12 10V2M4.93 10.93l1.41 1.41M2 18h2M20 18h2M19.07 10.93l-1.41 1.41M22 22H2M8 6l4-4 4 4M16 18a4 4 0 0 0-8 0',
  night: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z',
};

/** Where home's working hours fall there, drawn. */
function PartIcon({ part }: { part: Verdict }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PART_PATHS[part]} />
    </svg>
  );
}

/**
 * A clock's time, pressed to type another in its place, as large: the clocks move as soon as what
 * is typed reads; Enter or leaving the field keeps it, Escape goes back.
 */
function TimeEdit({
  minutes,
  label,
  onTime,
}: {
  minutes: number;
  label: string;
  onTime: (minutes: number) => void;
}) {
  // What is typed, and the time it began from: Escape goes back to it.
  const [typing, setTyping] = useState<{ text: string; from: number } | null>(null);
  return typing === null ? (
    <button
      type="button"
      className="clock-time mono"
      aria-label={`${label}: ${clockOf(minutes)}`}
      onClick={() => {
        setTyping({ text: clockOf(minutes), from: minutes });
      }}
    >
      {clockOf(minutes)}
    </button>
  ) : (
    <input
      className="clock-time mono"
      aria-label={label}
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      // The field takes the time's place at once, its text ready to be typed over.
      autoFocus
      onFocus={(event) => {
        event.target.select();
      }}
      value={typing.text}
      // Each time that reads moves the clocks at once, as it is typed.
      // Only a time's figures and its colon are taken: never 9:100.
      onChange={(event) => {
        const text = event.target.value.replace(/[^\d:]/g, '');
        setTyping({ ...typing, text });
        const read = minutesOf(text);
        if (read !== null) onTime(wrap(read));
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onTime(typing.from);
        if (event.key === 'Enter' || event.key === 'Escape') setTyping(null);
      }}
      onBlur={() => {
        setTyping(null);
      }}
    />
  );
}

/**
 * A time typed, or one of the day's half hours picked from under the field: kept once it reads,
 * the field saying how to write it until it does.
 */
function TimeField({
  label,
  minutes,
  onTime,
}: {
  label: string;
  minutes: number;
  onTime: (minutes: number) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(clockOf(minutes));
  const typed = text.trim();
  // As kept, every half hour; as being typed, those that begin with it.
  const offered = HALVES.map(clockOf).filter(
    (one) => typed === clockOf(minutes) || one.startsWith(typed),
  );
  return (
    <div className="time-field">
      <Suggest
        label={label}
        value={text}
        openEmpty
        most={HALVES.length}
        suggestions={offered.map((one) => ({ value: one, label: one }))}
        onChange={(value) => {
          setText(value);
          const read = minutesOf(value);
          if (read !== null) onTime(read);
        }}
      />
      {minutesOf(text) === null && <p className="field-error">{t('clocks.timeWrong')}</p>}
    </div>
  );
}

/**
 * The trip's clocks against home's working day (the Clocks mockup): a time at home moved along
 * the day, home's working hours on each place's clock, each at its own dates — so a traveller who
 * works while away sees when, and whether by day, in the evening or at night.
 */
export function Clocks() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [search] = useSearchParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [work, setWorkHours] = useState<WorkHours>({ from: 540, to: 1020 });
  const [picked, setPicked] = useState<string[] | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [at, setAt] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Whether home's working hours are drawn and said: the clocks serve one who does not work too.
  const [working, setWorking] = useState(true);

  useEffect(() => {
    getPlan(id).then(
      (plan) => {
        setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    // Nine to five until it is read.
    getWork().then(setWorkHours, () => undefined);
  }, [id, changed]);

  const home = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const homeCity = home.slice(home.lastIndexOf('/') + 1).replaceAll('_', ' ');
  // Only the places on another clock than home's: at home's own time there is nothing to
  // convert. Worked out once for the plan, not each time the time picked moves.
  const places = useMemo(() => {
    const here = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (state.kind === 'ready' ? placesOf(state.plan) : []).filter(
      (one) => one.offset - offsetMinutes(here, one.first) !== 0,
    );
  }, [state]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }

  const today = toIso(new Date());
  // Each country once, in the order the trip comes to it; its clocks from the westmost, each with
  // its places on it.
  const countries: { country: string; clocks: Clock[] }[] = [];
  for (const one of places) {
    const known = countries.find((c) => c.country === one.country);
    const entry = known ?? { country: one.country, clocks: [] };
    if (!known) countries.push(entry);
    const key = `${one.country} ${String(one.offset)}`;
    const clock = entry.clocks.find((c) => c.key === key);
    if (clock) clock.places.push(one);
    else
      entry.clocks.push({
        key,
        country: one.country,
        offset: one.offset,
        zone: one.zone,
        places: [one],
      });
  }
  for (const entry of countries) entry.clocks.sort((a, b) => a.offset - b.offset);
  const clocks = countries.flatMap((entry) => entry.clocks);
  // The clocks compared: those picked; at first the one asked for by its country, else where the
  // trip is today, else its first.
  const asked = search.get('country');
  const start =
    places.find((one) =>
      asked === null ? one.first <= today && today < one.last : one.country === asked,
    ) ?? places.at(0);
  const keys = picked ?? (start ? [`${start.country} ${String(start.offset)}`] : []);
  const shown = clocks.filter((clock) => keys.includes(clock.key));
  const toggle = (key: string) => {
    setPicked(keys.includes(key) ? keys.filter((one) => one !== key) : [...keys, key]);
  };
  // The day the two days begin: the one picked, else today while at the first clock compared,
  // else its first day there; today when none is compared.
  const lead = shown.at(0)?.places ?? [];
  const here = lead.find((one) => one.first <= today && today < one.last);
  const [opening] = lead;
  const first = date ?? (opening && !here ? opening.first : today);
  // Each clock's offset from home's on that day, as summer time is kept there and at home.
  const shiftAt = (zone: string) => offsetMinutes(zone, first) - offsetMinutes(home, first);
  const now = at ?? work.from;
  const weekday = new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  // The day a minute of the two falls on, from the first's midnight: `Mon 16 Nov`.
  const dayOf = (minute: number) =>
    weekday.format(Date.parse(`${plusDays(first, Math.floor(minute / DAY))}T00:00:00Z`));

  // Kept once the start is before the end; shown as typed until then.
  const keep = (next: WorkHours) => {
    setWorkHours(next);
    setFailed(false);
    if (next.from >= next.to) return;
    setWork(next).then(setWorkHours, () => {
      setFailed(true);
    });
  };

  // A clock's two days as home's hours run: each hour's part there, home's working hours and
  // the time picked marked over it.
  const row = (label: ReactNode, detail: string, shift: number, place: string) => (
    <div className="clock-row">
      <div className="clock-place">
        <strong>{label}</strong>
        <span className="quiet mono">{detail}</span>
      </div>
      <div className="clock-now">
        {/* Pressed, the time is typed in its place: the slider moves to it, on its day. */}
        <TimeEdit
          minutes={wrap(now + shift)}
          label={t('clocks.timeAt', { place })}
          onTime={(typed) => {
            const there = now + shift;
            const next = there - wrap(there) + typed - shift;
            setAt(Math.min(Math.max(next, 0), 2 * DAY - 15));
          }}
        />
        <span className="clock-part">
          <DayPartIcon part={partOf(now + shift)} />
          {t(`clocks.key.${partOf(now + shift)}`)}
        </span>
        <span className="quiet">{dayOf(now + shift)}</span>
      </div>
      <div className="clock-strip" aria-hidden="true">
        <Strip shift={shift} work={working ? work : null} />
        <span
          className="clock-cursor"
          style={{ left: `${String((now / (2 * DAY)) * 100)}%` }}
        />
      </div>
    </div>
  );

  // A day of the trip under its number: the country it is spent in, and a way taken that day.
  const noteOf = (day: string): DayNote | null => {
    const stay = state.plan.stops.find(
      (stop) => !stop.transfer && stop.start <= day && day < plusDays(stop.start, stop.days),
    );
    const way = state.plan.stops.find(
      (stop) => stop.out !== null && plusDays(stop.start, stop.days) === day,
    );
    if (!stay && !way) return null;
    const where = stay ? `${countryName(stay.country, lang)} · ${stay.city}` : '';
    const how = way?.out ? t(`mode.${way.out.mode}`) : '';
    return {
      mark: stay?.country ?? '',
      said: [where, how].filter((part) => part !== '').join(' · '),
      icon: way?.out ? <ModeIcon mode={way.out.mode} size={10} /> : undefined,
    };
  };

  // Home's working hours, set behind the icon at the panel's top right.
  const settings = (
    <Popover
      open={settingsOpen}
      onOpenChange={setSettingsOpen}
      label={t('clocks.home', { city: homeCity })}
      trigger={(props) => (
        <button
          type="button"
          className="icon-button clocks-settings"
          aria-label={t('clocks.home', { city: homeCity })}
          title={t('clocks.home', { city: homeCity })}
          aria-expanded={settingsOpen}
          {...props}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* After Lucide's sliders-horizontal (ISC licence). */}
            <path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3M14 2v4M8 10v4M16 18v4" />
          </svg>
        </button>
      )}
    >
      <div className="clocks-work">
        <h3>{t('clocks.home', { city: homeCity })}</h3>
        {/* Working while away, or not: without it the clocks are only the places' times. */}
        <label className="check">
          <input
            type="checkbox"
            checked={working}
            onChange={(event) => {
              setWorking(event.target.checked);
            }}
          />
          {t('clocks.working')}
        </label>
        <TimeField
          label={t('clocks.from')}
          minutes={work.from}
          onTime={(from) => {
            keep({ ...work, from });
          }}
        />
        <TimeField
          label={t('clocks.to')}
          minutes={work.to}
          onTime={(to) => {
            keep({ ...work, to });
          }}
        />
        {work.from >= work.to && (
          <p className="field-error" role="alert">
            {t('clocks.order')}
          </p>
        )}
        {failed && (
          <p className="field-error" role="alert">
            {t('plan.failed')}
          </p>
        )}
      </div>
    </Popover>
  );

  return (
    <div className="page clocks-page">
      <header className="page-head">
        <h1>{t('clocks.title')}</h1>
      </header>
      <section className="panel clocks-panel" aria-label={t('clocks.title')}>
        {clocks.length > 0 ? (
          <>
            {/* Each country once, its clocks side by side in it: each pressed to compare it. */}
            <div className="clocks-top">
              <div className="clocks-places" role="group" aria-label={t('clocks.place')}>
                {countries.map(({ country, clocks: kept }) => {
                  const said = (clock: Clock) =>
                    clock.places
                      .map(
                        (one) =>
                          `${one.towns.join(', ')} · ${dayRange(one.first, one.last, lang)}`,
                      )
                      .join('\n');
                  const label = countryName(country, lang);
                  return kept.length === 1 ? (
                    kept.map((only) => (
                      <button
                        key={country}
                        type="button"
                        className="chip"
                        title={said(only)}
                        aria-pressed={keys.includes(only.key)}
                        onClick={() => {
                          toggle(only.key);
                        }}
                      >
                        {label}
                      </button>
                    ))
                  ) : (
                    <span
                      key={country}
                      className="clocks-country"
                      role="group"
                      aria-label={t('clocks.zones', { country: label })}
                    >
                      <span className="clocks-country-name">{label}</span>
                      {kept.map((clock) => (
                        <button
                          key={clock.key}
                          type="button"
                          title={said(clock)}
                          aria-label={`${label} ${utcOffset(clock.offset)}`}
                          aria-pressed={keys.includes(clock.key)}
                          onClick={() => {
                            toggle(clock.key);
                          }}
                        >
                          {utcOffset(clock.offset).replace('UTC', '')}
                        </button>
                      ))}
                    </span>
                  );
                })}
              </div>
              {settings}
            </div>
            <div className="clock-rows">
              {/* The day, and the time at home moved over the strips, as wide as they are. */}
              <div className="clock-row clock-row-head">
                <div className="clock-date">
                  {/* Each day says where it is spent and a way taken; a day picked adds its clock. */}
                  <DatePick
                    label={t('clocks.date')}
                    value={first}
                    note={noteOf}
                    onChange={(value) => {
                      setDate(value);
                      const there = clocks.find((clock) =>
                        clock.places.some(
                          (one) => one.first <= String(value) && String(value) < one.last,
                        ),
                      );
                      if (there && !keys.includes(there.key)) setPicked([there.key, ...keys]);
                    }}
                  />
                </div>
                <div className="clocks-slider">
                  {/* The track in the first clock's parts of the day, as wide as the strips: where
                      it is best to work. The slider over it is half a thumb wider each side. */}
                  <span
                    className="clocks-track"
                    aria-hidden="true"
                    style={{
                      background: trackOf([0, ...shown.map((clock) => shiftAt(clock.zone))]),
                    }}
                  />
                  <input
                    type="range"
                    min={0}
                    max={2 * DAY - 1}
                    step={1}
                    value={now}
                    aria-label={t('clocks.at', { city: homeCity })}
                    aria-valuetext={`${dayOf(now)} ${clockOf(wrap(now))}`}
                    onChange={(event) => {
                      setAt(Number(event.target.value));
                    }}
                  />
                </div>
              </div>
              {row(homeCity, utcOffset(offsetMinutes(home, first)), 0, homeCity)}
              {/* A country's name once, over its clocks; each next one said by its offset. */}
              {shown.map((clock, index) => {
                const country = countryName(clock.country, lang);
                const again = shown[index - 1]?.country === clock.country;
                return (
                  <Fragment key={clock.key}>
                    {row(
                      again ? <span className="visually-hidden">{country}</span> : country,
                      `${utcOffset(offsetMinutes(clock.zone, first))} · ${shiftSaid(shiftAt(clock.zone))}`,
                      shiftAt(clock.zone),
                      `${country} ${utcOffset(offsetMinutes(clock.zone, first))}`,
                    )}
                  </Fragment>
                );
              })}
            </div>
            <ul className="clocks-key">
              {/* Each part of the day by its icon, in its colour, with its hours. */}
              {PARTS.map(([part, from], index) => (
                <li key={part}>
                  <span className={`clocks-key-part part-${part}`} aria-hidden="true">
                    <DayPartIcon part={part} />
                  </span>
                  {t(`clocks.key.${part}`)}
                  <span className="mono">
                    {String(from).padStart(2, '0')}–
                    {String(PARTS[index + 1]?.[1] ?? 24).padStart(2, '0')}
                  </span>
                </li>
              ))}
              <li>
                <i className="clock-work-key" aria-hidden="true" />
                {t('clocks.key.work')}
              </li>
            </ul>
          </>
        ) : (
          <p className="quiet">{t('clocks.none')}</p>
        )}
      </section>
      {places.length > 0 && (
        <PlacesTable places={places} home={home} work={work} working={working} />
      )}
    </div>
  );
}

/**
 * A clock's two days as home's hours run: each hour's part of the day there, every third hour
 * numbered, and home's working hours over it — drawn again only when its clock or the hours
 * change, not as the time picked moves.
 */
const Strip = memo(function Strip({ shift, work }: { shift: number; work: WorkHours | null }) {
  return (
    <>
      {Array.from({ length: HOURS }, (_, hour) => {
        const local = hour * 60 + shift;
        return (
          <i key={hour} className={`part-${partOf(local)}`}>
            {hour % 3 === 0 ? clockOf(wrap(local)).slice(0, 2) : ''}
          </i>
        );
      })}
      {work &&
        [0, 1].map((day) => (
          <span
            key={day}
            className="clock-work"
            style={{
              left: `${String(((day * DAY + work.from) / (2 * DAY)) * 100)}%`,
              width: `${String(((work.to - work.from) / (2 * DAY)) * 100)}%`,
            }}
          />
        ))}
    </>
  );
});

/** Every place of the trip on another clock than home's: its days, its clock, and home's
 * working hours there — not drawn again as the time picked moves. */
const PlacesTable = memo(function PlacesTable({
  places,
  home,
  work,
  working,
}: {
  places: readonly Place[];
  home: string;
  work: WorkHours;
  working: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <section className="panel" aria-label={t('clocks.everywhere')}>
      <div className="table-scroll">
        <table className="clocks-table">
          <thead>
            <tr>
              <th scope="col">{t('clocks.place')}</th>
              <th scope="col">{t('clocks.dates')}</th>
              <th scope="col">{t('clocks.clock')}</th>
              {working && <th scope="col">{t('clocks.workThere')}</th>}
            </tr>
          </thead>
          <tbody>
            {places.map((one) => {
              const shift = one.offset - offsetMinutes(home, one.first);
              const verdict = verdictOf(work.from, work.to, shift);
              return (
                <tr key={`${one.first}-${one.country}`}>
                  <th scope="row">
                    {countryName(one.country, lang)}
                    <span className="quiet"> · {one.towns.join(', ')}</span>
                  </th>
                  <td className="mono">{dayRange(one.first, one.last, lang)}</td>
                  <td className="mono">
                    {utcOffset(one.offset)} · {shiftSaid(shift)}
                  </td>
                  {/* Home's working hours there, and by its icon whether by day, in the
                      evening or at night: said in full when pointed at. */}
                  {working && (
                    <td>
                      <span className="clocks-work-cell">
                        <span className="mono">
                          {clockOf(work.from + shift)}–{clockOf(work.to + shift)}
                        </span>
                        <span
                          className={`verdict verdict-${verdict}`}
                          title={t(`clocks.part.${verdict}`)}
                        >
                          <PartIcon part={verdict} />
                          <span className="visually-hidden">{t(`clocks.part.${verdict}`)}</span>
                        </span>
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
});
